import React, { useState } from "react";
import PropTypes from "prop-types";

// @mui
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

// components
import Iconify from "src/components/iconify";
import { useLocales } from "src/locales";
import enqueueSnackbar from "src/utils/notify";

// hooks
import {
  useAudioRecorder,
  STOP_REASON,
  RECORDER_ERROR,
  SILENCE_TIMEOUT_MS,
  MAX_DURATION_MS,
} from "src/hooks/use-audio-recorder";

// api
import { transcriptSpeech, interpretTranscript } from "src/api/ai";
import { AI_SOURCE_AUDIO } from "src/core/lib/constants";

import VoiceLevelHalo from "./voiceLevelHalo";
import VoiceRecorderBar from "./voiceRecorderBar";
import VoiceTranscriptDialog from "./voiceTranscriptDialog";

// ----------------------------------------------------------------------

// What happens after the recorder handed us the audio. Recording and starting come from the hook, so a
// single enum here cannot contradict it.
const PHASE = {
  idle: "idle",
  transcribing: "transcribing",
  confirming: "confirming",
  interpreting: "interpreting",
};

// ----------------------------------------------------------------------

export default function SpeechToTextButton({
  onTranscription,
  source = AI_SOURCE_AUDIO.textField,
  getContext = null,
  disabled = false,
  variant = "inline",
  confirmTranscript = true,
  interpret = true,
  tooltip = null,
}) {
  const { t } = useLocales();

  const [phase, setPhase] = useState(PHASE.idle);
  const [transcript, setTranscript] = useState("");

  const reset = () => {
    setPhase(PHASE.idle);
    setTranscript("");
  };

  const handleError = code => {
    if (code === RECORDER_ERROR.unsupported)
      enqueueSnackbar(t("speechRecognitionNotSupported"), { variant: "warning" });
    else if (code === RECORDER_ERROR.notFound)
      enqueueSnackbar(t("microphoneNotFound"), { variant: "warning" });
    else if (code === RECORDER_ERROR.permission)
      enqueueSnackbar(t("microphonePermissionDenied"), { variant: "warning" });
    else if (code === RECORDER_ERROR.tooLarge)
      enqueueSnackbar(t("voiceRecordingTooLarge"), { variant: "warning" });
    else enqueueSnackbar(t("voiceNothingHeard"), { variant: "warning" });

    reset();
  };

  const applyTranscript = async text => {
    // A plain text field or an autocomplete search box needs no interpretation: the transcript is the
    // value, and it lands somewhere the user can still edit.
    if (!interpret) {
      onTranscription({ transcription: text, source, success: true });
      reset();
      return;
    }

    try {
      setPhase(PHASE.interpreting);

      // Read the extra context (e.g. the WorkRequest field list) just-in-time so async-loaded
      // configuration is current at send time.
      const response = await interpretTranscript(text, getContext?.(), source);

      // success === false means the text was understood as speech but no value or field could be
      // resolved from it; keep the dialog open so the user can rephrase instead of dictating again.
      if (response?.success) {
        onTranscription(response);

        // Some fields applied, but the API still has something to say (a field it had to skip, and
        // why). Surfacing it is the difference between "half of it worked" and "why did that not fill".
        if (response.message) enqueueSnackbar(response.message, { variant: "info" });

        reset();
      } else {
        // The API explains itself whenever it can: an unmet prerequisite, a value matching no record,
        // a field named without a value. Only fall back to the generic message when it does not.
        enqueueSnackbar(response?.message || t("speechNotUnderstood"), { variant: "warning" });
        setPhase(confirmTranscript ? PHASE.confirming : PHASE.idle);
      }
    } catch (error) {
      console.log(error);
      // The axios interceptor already surfaces the error to the user.
      setPhase(confirmTranscript ? PHASE.confirming : PHASE.idle);
    }
  };

  const handleComplete = async ({ blob, fileName, stopReason }) => {
    // Say why we stopped whenever we stopped on our own, so the user is never left wondering.
    if (stopReason === STOP_REASON.silence)
      enqueueSnackbar(t("voiceStoppedBySilence", { seconds: SILENCE_TIMEOUT_MS / 1000 }), {
        variant: "info",
      });
    if (stopReason === STOP_REASON.maxDuration)
      enqueueSnackbar(t("voiceMaxDurationReached", { seconds: MAX_DURATION_MS / 1000 }), {
        variant: "info",
      });

    try {
      setPhase(PHASE.transcribing);

      const response = await transcriptSpeech(blob, fileName);
      const text = response?.transcription?.trim();

      if (!response?.success || !text) {
        enqueueSnackbar(t("speechNotUnderstood"), { variant: "warning" });
        reset();
        return;
      }

      setTranscript(text);

      if (confirmTranscript) setPhase(PHASE.confirming);
      else await applyTranscript(text);
    } catch (error) {
      console.log(error);
      // The axios interceptor already surfaces the error to the user.
      reset();
    }
  };

  const recorder = useAudioRecorder({ onComplete: handleComplete, onError: handleError });

  const listening = recorder.isRecording || recorder.isStarting;
  const busy = phase === PHASE.transcribing || phase === PHASE.interpreting;
  const label = tooltip ?? t("voiceAssistant");

  let statusLabel = label;
  if (phase === PHASE.transcribing) statusLabel = t("voiceTranscribing");
  else if (phase === PHASE.interpreting) statusLabel = t("voiceInterpreting");
  else if (listening) statusLabel = t("voiceListening");

  const handleClick = () => {
    if (busy) return;

    if (recorder.isRecording) recorder.stop(STOP_REASON.manual);
    else recorder.start();
  };

  let tooltipTitle = label;
  if (listening) tooltipTitle = t("stopRecording");
  else if (busy) tooltipTitle = statusLabel;

  const micButton = (
    <Tooltip title={tooltipTitle}>
      <span>
        <IconButton
          type="button"
          color={listening ? "error" : "primary"}
          aria-label={label}
          onClick={handleClick}
          disabled={disabled || busy}
          sx={{ position: "relative" }}
        >
          {busy ? (
            <CircularProgress size={20} />
          ) : (
            <>
              {/* Mounted only while listening: its frame loop lives as long as it is mounted. */}
              {listening && <VoiceLevelHalo levelRef={recorder.levelRef} />}
              <Iconify
                icon={listening ? "mdi:stop" : "mdi:microphone"}
                sx={{ position: "relative" }}
              />
            </>
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <>
      {variant === "bar" && (listening || busy) ? (
        <VoiceRecorderBar
          levelRef={recorder.levelRef}
          recording={recorder.isRecording}
          processing={busy}
          statusLabel={statusLabel}
          startedAt={recorder.startedAt}
          onCancel={() => {
            recorder.stop(STOP_REASON.cancelled);
            reset();
          }}
          onSend={() => recorder.stop(STOP_REASON.manual)}
        />
      ) : (
        micButton
      )}

      {/* Mounted only when needed: a form can render many speech-enabled fields, and an
          always-mounted Dialog per field would mean one portal each. */}
      {(phase === PHASE.confirming || phase === PHASE.interpreting) && (
        <VoiceTranscriptDialog
          open
          value={transcript}
          onChange={setTranscript}
          loading={phase === PHASE.interpreting}
          onConfirm={() => applyTranscript(transcript)}
          onCancel={reset}
          onRecordAgain={() => {
            reset();
            recorder.start();
          }}
        />
      )}
    </>
  );
}

SpeechToTextButton.propTypes = {
  onTranscription: PropTypes.func.isRequired,
  source: PropTypes.number,
  getContext: PropTypes.func,
  disabled: PropTypes.bool,
  /** "inline" keeps the IconButton footprint; "bar" shows the wide waveform pill. */
  variant: PropTypes.oneOf(["inline", "bar"]),
  /** Show the editable confirmation dialog before applying anything. */
  confirmTranscript: PropTypes.bool,
  /** Send the transcript for interpretation; false when the transcript is itself the value. */
  interpret: PropTypes.bool,
  tooltip: PropTypes.string,
};
